import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { throttle } from 'lodash';

import { Container, Loader } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/StudentCourseDrivenExperience.less';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { ASSIGNMENT_MENU_STATUS, ASSIGNMENT_STATUS } from '../managers/AssignmentManager';

import Auth from '../managers/AuthManager';

import StudentHelperService from '../services/StudentHelperService';

const StudentCourseDrivenExperienceView = observer((props = {}) => {
  const { loadingStudentDashboard } = props;

  const {
    appManager,
    assignmentManager,
    classroomManager,
    courseManager,
    studentCourseDrivenExperienceManager,
    userManager
  } = useContext(MobXProviderContext);

  const allowStudentCourseDrivenExperience = true;

  const { currentClassroomId } = classroomManager;

  const courseList = courseManager.getCourseList(currentClassroomId);

  const throttledFetchStudentCourseDrivenAssignmentsRef = useRef();

  const StudentCourseDrivenAssignments = SatCoreComponent('StudentCourseDrivenAssignments');
  const StudentCourseDrivenCarousel = SatCoreComponent('StudentCourseDrivenCarousel');

  const throttledFetchStudentCourseDrivenAssignments = useCallback(() => {
    return throttle(async ({ assignmentMenuStatus, courseId }) => {
      if (appManager.initializingApp) {
        return;
      }
      try {
        studentCourseDrivenExperienceManager.setLoadingStudentCourseDrivenExperience(true);

        let today = null;
        let status = [];

        // CURRENT assignments
        if (assignmentMenuStatus === ASSIGNMENT_MENU_STATUS.CURRENT) {
          status = [ASSIGNMENT_STATUS.LOCKED, ASSIGNMENT_STATUS.READY, ASSIGNMENT_STATUS.STARTED];
        }
        // DUE TODAY assignments
        if (assignmentMenuStatus === ASSIGNMENT_MENU_STATUS.TODAY) {
          today = Auth.getToday();
        }
        // CLOSED assignments
        if (assignmentMenuStatus === ASSIGNMENT_MENU_STATUS.CLOSED) {
          status = [ASSIGNMENT_STATUS.CLOSED];
        }
        // COMPLETED (GRADED) assignments
        if (assignmentMenuStatus === ASSIGNMENT_MENU_STATUS.GRADED) {
          status = [ASSIGNMENT_STATUS.COMPLETED];
        }
        assignmentManager.setAssignmentStatus(status);

        await StudentHelperService.fetchStudentAssignments({
          classroomId: classroomManager.currentClassroomId,
          courseContentItemId: courseId,
          dueToday: today !== null,
          status
        });
      } finally {
        studentCourseDrivenExperienceManager.setLoadingStudentCourseDrivenExperience(false);
      }
    }, 1250);
  }, []);

  useEffect(() => {
    // Initialize throttled fetch function, which will be called via fetchStudentCourseDrivenAssignments when applicable
    if (!throttledFetchStudentCourseDrivenAssignmentsRef.current) {
      throttledFetchStudentCourseDrivenAssignmentsRef.current = throttledFetchStudentCourseDrivenAssignments();
    }
  }, []);

  const handleChangeSelectedCourse = async ({ courseIndex } = {}) => {
    const { selectedCourseDrivenCarouselIndex } = studentCourseDrivenExperienceManager;
    if (typeof courseIndex !== 'number' || courseIndex === selectedCourseDrivenCarouselIndex) {
      return;
    }
    studentCourseDrivenExperienceManager.setSelectedCourseDrivenCarouselIndex(courseIndex);

    const selectedCourse = courseList[courseIndex];

    courseManager.setCurrentCourseId(selectedCourse.id);
    courseManager.setCurrentCourseName(selectedCourse.name);

    const {
      selectedCourseDrivenAssignmentMenuStatus: selectedAssignmentMenuStatus
    } = studentCourseDrivenExperienceManager;

    await fetchStudentCourseDrivenAssignments({
      assignmentMenuStatus: selectedAssignmentMenuStatus,
      courseId: selectedCourse.id
    });
  };

  const handleChangeSelectedAssignmentMenuStatus = async ({ assignmentMenuStatus } = {}) => {
    const {
      selectedCourseDrivenAssignmentMenuStatus: selectedAssignmentMenuStatus
    } = studentCourseDrivenExperienceManager;
    if (!assignmentMenuStatus || assignmentMenuStatus === selectedAssignmentMenuStatus) {
      return;
    }
    studentCourseDrivenExperienceManager.setSelectedCourseDrivenAssignmentMenuStatus(assignmentMenuStatus);

    const { selectedCourseDrivenCarouselIndex } = studentCourseDrivenExperienceManager;
    const selectedCourse = courseList[selectedCourseDrivenCarouselIndex];

    await fetchStudentCourseDrivenAssignments({
      assignmentMenuStatus,
      courseId: courseManager.currentCourseId || selectedCourse?.id
    });
  };

  const fetchStudentCourseDrivenAssignments = useCallback(async (params) => {
    studentCourseDrivenExperienceManager.setLoadingStudentCourseDrivenExperience(true);
    await throttledFetchStudentCourseDrivenAssignmentsRef.current(params);
  }, []);

  const renderCarouselWidget = () => {
    return (
      <StudentCourseDrivenCarousel
        courseList={courseList}
        handleChangeSelectedCourse={handleChangeSelectedCourse} />
    );
  };

  const renderAssignmentsWidget = () => {
    return (
      <StudentCourseDrivenAssignments
        handleChangeSelectedAssignmentMenuStatus={handleChangeSelectedAssignmentMenuStatus}
        handlePresent={props.handlePresent}
        renderAssignmentCards={props.renderAssignmentCards}
      />
    );
  };

  const showSpinnerAndHideView = (
    loadingStudentDashboard || appManager.initializingApp
  );
  if (showSpinnerAndHideView) {
    return <Loader active />;
  } else {
    return (
      <Container className={classNames('StudentCourseDrivenExperienceView dashboard-backdrop', 'student-dashboard-backdrop', {
        'student-assignment-view-container': allowStudentCourseDrivenExperience,
        'student-course-driven-experience-dashboard-backdrop': allowStudentCourseDrivenExperience
      })} fluid>
        <Container className={classNames('dashboard-container', 'student-dashboard-container', {
          'student-course-driven-experience-dashboard-container': allowStudentCourseDrivenExperience
        })}>
          {/* {showSpinnerAndShowView && <Loader active />} */}
          <div className='headerContainer'>
            <div className='headerWrapper'>
              {/* placeholder */}
            </div>
          </div>
          <Container className={classNames('widget-wrapper', {
            'student-course-driven-experience-dashboard-widget-wrapper': allowStudentCourseDrivenExperience
          })}>
            {(userManager.loaded && classroomManager.loaded) ? (
              <>
                <Container className='left-col'>
                  {renderCarouselWidget()}
                </Container>
                <Container className='right-col'>
                  {renderAssignmentsWidget()}
                </Container>
              </>
            ) : null}
          </Container>
        </Container>
      </Container>
    );
  }
});
export default StudentCourseDrivenExperienceView;

SatCoreRegister('StudentCourseDrivenExperienceView', StudentCourseDrivenExperienceView);
